import { CONTACT_EMAIL } from '../config/contact.js';

const SUPPORTED_LEGAL_JURISDICTIONS = new Set(['AT']);
const DEFAULT_JURISDICTION = 'AT';
const DEFAULT_WAITLIST_CONSENT_VERSION = 'waitlist-launch-notification-v1';

function readEnvValue(name) {
  const viteValue = import.meta.env?.[name];
  if (typeof viteValue === 'string' && viteValue.trim()) {
    return viteValue;
  }

  const processValue =
    typeof process !== 'undefined' && process.env
      ? process.env[name]
      : undefined;
  if (typeof processValue === 'string' && processValue.trim()) {
    return processValue;
  }

  if (typeof viteValue === 'string') {
    return viteValue;
  }

  if (typeof processValue === 'string') {
    return processValue;
  }

  return '';
}

function normalizeText(value) {
  const normalized = String(value || '').trim();
  return normalized || '';
}

function normalizeOptionalText(value) {
  const normalized = normalizeText(value);
  return normalized || null;
}

function normalizeJurisdiction(value) {
  const normalized = normalizeText(value).toUpperCase();
  if (SUPPORTED_LEGAL_JURISDICTIONS.has(normalized)) {
    return normalized;
  }

  return DEFAULT_JURISDICTION;
}

function buildAddressLines({
  street,
  postalCode,
  city,
  country,
}) {
  const streetLine = normalizeOptionalText(street);
  const postalCodeValue = normalizeOptionalText(postalCode);
  const cityValue = normalizeOptionalText(city);
  const countryValue = normalizeOptionalText(country);

  const localityLine = [postalCodeValue, cityValue].filter(Boolean).join(' ');

  return [streetLine, localityLine, countryValue].filter(Boolean);
}

function parseIsoDate(value) {
  const normalized = normalizeText(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }

  const parsed = new Date(`${normalized}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return normalized;
}

const legalConfig = Object.freeze({
  projectName: 'cedrum',
  jurisdiction: normalizeJurisdiction(readEnvValue('VITE_LEGAL_JURISDICTION')),
  lastUpdatedDate: parseIsoDate(readEnvValue('VITE_LEGAL_LAST_UPDATED_DATE')) || '2026-03-07',
  waitlistConsentVersion:
    normalizeOptionalText(readEnvValue('VITE_WAITLIST_CONSENT_VERSION')) || DEFAULT_WAITLIST_CONSENT_VERSION,
  operator: Object.freeze({
    name: normalizeOptionalText(readEnvValue('VITE_LEGAL_OPERATOR_NAME')),
    country: normalizeOptionalText(readEnvValue('VITE_LEGAL_OPERATOR_COUNTRY')),
    address: Object.freeze({
      street: normalizeOptionalText(readEnvValue('VITE_LEGAL_OPERATOR_STREET')),
      postalCode: normalizeOptionalText(readEnvValue('VITE_LEGAL_OPERATOR_POSTAL_CODE')),
      city: normalizeOptionalText(readEnvValue('VITE_LEGAL_OPERATOR_CITY')),
      country: normalizeOptionalText(readEnvValue('VITE_LEGAL_OPERATOR_COUNTRY')),
    }),
    addressLines: buildAddressLines({
      street: readEnvValue('VITE_LEGAL_OPERATOR_STREET'),
      postalCode: readEnvValue('VITE_LEGAL_OPERATOR_POSTAL_CODE'),
      city: readEnvValue('VITE_LEGAL_OPERATOR_CITY'),
      country: readEnvValue('VITE_LEGAL_OPERATOR_COUNTRY'),
    }),
    email: CONTACT_EMAIL,
    phone: normalizeOptionalText(readEnvValue('VITE_LEGAL_CONTACT_PHONE')),
    legalForm: normalizeOptionalText(readEnvValue('VITE_LEGAL_LEGAL_FORM')),
    registerNumber: normalizeOptionalText(readEnvValue('VITE_LEGAL_REGISTER_NUMBER')),
    registerCourt: normalizeOptionalText(readEnvValue('VITE_LEGAL_REGISTER_COURT')),
    vatId: normalizeOptionalText(readEnvValue('VITE_LEGAL_VAT_ID')),
  }),
  mediaDisclosure: Object.freeze({
    purpose: normalizeOptionalText(readEnvValue('VITE_LEGAL_MEDIA_PURPOSE')),
  }),
  providers: Object.freeze([
    Object.freeze({
      id: 'cloudflare-hosting',
      name: 'Cloudflare',
      role: 'Hosting, CDN und technische Sicherheit der Website',
      data: 'Verbindungs- und Sicherheitsmetadaten (z. B. IP-Adresse, Request-Metadaten, Fehlerdaten)',
      location: 'Globales Netzwerk; Verarbeitung kann auch außerhalb des EWR stattfinden',
      policyUrl: 'https://www.cloudflare.com/privacypolicy/',
      alwaysRelevant: true,
    }),
    Object.freeze({
      id: 'cloudflare-turnstile',
      name: 'Cloudflare Turnstile',
      role: 'Bot-Schutz für das Waitlist-Formular',
      data: 'Technische Verifizierungsdaten bei Formularabsendung',
      location: 'Cloudflare-Infrastruktur (global)',
      policyUrl: 'https://www.cloudflare.com/privacypolicy/',
      alwaysRelevant: Boolean(normalizeText(readEnvValue('VITE_TURNSTILE_SITE_KEY'))),
    }),
    Object.freeze({
      id: 'resend',
      name: 'Resend',
      role: 'Versand der Bestätigungs-E-Mail und Pflege der Waitlist-Kontakte',
      data: 'E-Mail-Adresse, Zeitstempel, sprachbezogene Metadaten',
      location: 'Resend-Infrastruktur; je nach Konfiguration auch außerhalb des EWR möglich',
      policyUrl: 'https://resend.com/legal/privacy-policy',
      alwaysRelevant: true,
    }),
  ]),
});

export { legalConfig };
